/**
 * PendingApproval.jsx
 * Shown after OTP is verified — user waits for admin approval.
 * Features animated treasury background and smooth transition.
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";

// ── ANIMATED BACKGROUND CANVAS ────────────────────────────────────────────────
function TreasuryCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Floating particles
    const particles = Array.from({ length: 60 }, () => ({
      x:    Math.random() * canvas.width,
      y:    Math.random() * canvas.height,
      r:    Math.random() * 2 + 0.5,
      dx:   (Math.random() - 0.5) * 0.4,
      dy:   (Math.random() - 0.5) * 0.4,
      opacity: Math.random() * 0.5 + 0.1,
    }));

    // Candlestick data
    const candles = Array.from({ length: 12 }, (_, i) => ({
      x:     60 + i * 80,
      open:  Math.random() * 100 + 200,
      close: Math.random() * 100 + 200,
      high:  0,
      low:   0,
    }));
    candles.forEach(c => {
      c.high = Math.max(c.open, c.close) + Math.random() * 30;
      c.low  = Math.min(c.open, c.close) - Math.random() * 30;
    });

    // Moving line chart points
    let linePoints = Array.from({ length: 20 }, (_, i) => ({
      x: i * 60,
      y: canvas.height * 0.6 + Math.sin(i * 0.5) * 60 + Math.random() * 40,
    }));

    let t = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Dark gradient background
      const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bg.addColorStop(0, "#0a0b0f");
      bg.addColorStop(1, "#0d0f18");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Grid lines
      ctx.strokeStyle = "rgba(99,102,241,0.05)";
      ctx.lineWidth = 1;
      for (let x = 0; x < canvas.width; x += 80) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
      }
      for (let y = 0; y < canvas.height; y += 80) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
      }

      // Moving line chart
      linePoints = linePoints.map((p, i) => ({
        x: p.x,
        y: canvas.height * 0.65 + Math.sin(t * 0.02 + i * 0.4) * 50 + Math.cos(t * 0.015 + i * 0.3) * 30,
      }));

      // Line chart fill
      ctx.beginPath();
      ctx.moveTo(linePoints[0].x, canvas.height);
      linePoints.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.lineTo(linePoints[linePoints.length - 1].x, canvas.height);
      ctx.closePath();
      const fillGrad = ctx.createLinearGradient(0, canvas.height * 0.4, 0, canvas.height);
      fillGrad.addColorStop(0, "rgba(99,102,241,0.15)");
      fillGrad.addColorStop(1, "rgba(99,102,241,0)");
      ctx.fillStyle = fillGrad;
      ctx.fill();

      // Line chart stroke
      ctx.beginPath();
      linePoints.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = "rgba(99,102,241,0.4)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Floating candlesticks
      candles.forEach((c, idx) => {
        const x   = ((c.x + t * 0.3) % (canvas.width + 100)) - 50;
        const bull = c.close > c.open;
        const color = bull ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)";
        const h   = Math.abs(c.close - c.open) || 4;

        // Wick
        ctx.strokeStyle = color;
        ctx.lineWidth   = 1;
        ctx.beginPath();
        ctx.moveTo(x, c.high * 0.4 + 100);
        ctx.lineTo(x, c.low  * 0.4 + 100);
        ctx.stroke();

        // Body
        ctx.fillStyle = color;
        ctx.fillRect(x - 6, Math.min(c.open, c.close) * 0.4 + 100, 12, h * 0.4 || 4);
      });

      // Floating currency labels
      const currencies = ["USD/INR", "EUR/INR", "GBP/INR", "JPY/INR", "AUD/INR"];
      currencies.forEach((cur, i) => {
        const x = ((i * 220 + t * 0.5) % (canvas.width + 200)) - 100;
        const y = 80 + i * 60;
        ctx.font = "11px monospace";
        ctx.fillStyle = `rgba(99,102,241,${0.15 + Math.sin(t * 0.02 + i) * 0.05})`;
        ctx.fillText(cur, x, y);
        const val = (84 + Math.sin(t * 0.01 + i) * 0.5).toFixed(2);
        ctx.fillStyle = `rgba(74,222,128,${0.15 + Math.sin(t * 0.02 + i) * 0.05})`;
        ctx.fillText(val, x + 70, y);
      });

      // Particles
      particles.forEach(p => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99,102,241,${p.opacity})`;
        ctx.fill();
      });

      // Floating numbers (treasury data)
      const nums = ["+2.3%", "-0.8%", "84.52", "₹5Cr", "+1.1%", "REPO 6.5%"];
      nums.forEach((n, i) => {
        const x = ((i * 180 + t * 0.3 + 100) % (canvas.width + 100)) - 50;
        const y = canvas.height - 60 - i * 40;
        ctx.font = "10px monospace";
        const isPos = n.startsWith("+");
        ctx.fillStyle = isPos
          ? `rgba(74,222,128,0.2)`
          : n.startsWith("-")
          ? `rgba(248,113,113,0.2)`
          : `rgba(251,191,36,0.2)`;
        ctx.fillText(n, x, y);
      });

      t++;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas ref={canvasRef} style={{
      position: "fixed", top: 0, left: 0,
      width: "100%", height: "100%",
      zIndex: 0, pointerEvents: "none",
    }} />
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function PendingApproval() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [status,      setStatus]      = useState("pending");
  const [userEmail,   setUserEmail]   = useState(location.state?.email || "");
  const [checking,    setChecking]    = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [countdown,   setCountdown]   = useState(10);
  const [dots,        setDots]        = useState("");

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => d.length >= 3 ? "" : d + ".");
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const init = async () => {
      let emailToUse = location.state?.email || "";
      if (!emailToUse) {
        const { data: { user } } = await supabase.auth.getUser();
        emailToUse = user?.email || "";
      }
      if (!emailToUse) { navigate("/login"); return; }
      setUserEmail(emailToUse);
      doCheck(emailToUse);
      const interval = setInterval(() => doCheck(emailToUse), 15000);
      return () => clearInterval(interval);
    };
    init();
  }, []);

  // Countdown when approved
  useEffect(() => {
    if (!transitioning) return;
    if (countdown <= 0) {
      navigate("/dashboard");
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [transitioning, countdown]);

  const doCheck = async (emailToCheck) => {
    if (!emailToCheck) return;
    setChecking(true);
    try {
      const { data } = await supabase
        .from("user_registrations")
        .select("status")
        .eq("email", emailToCheck)
        .maybeSingle();

      if (data?.status === "approved") {
        setStatus("approved");
        setTransitioning(true);
      } else if (data?.status === "rejected") {
        setStatus("rejected");
      }
    } catch (err) {
      console.error("Check status error:", err);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "sans-serif", padding: 24,
      position: "relative", overflow: "hidden",
    }}>
      {/* Animated background */}
      <TreasuryCanvas />

      {/* Card */}
      <div style={{
        position: "relative", zIndex: 10,
        background: "rgba(18,20,26,0.85)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(99,102,241,0.3)",
        borderRadius: 20, padding: "48px 44px",
        width: "100%", maxWidth: 480, textAlign: "center",
        boxShadow: "0 0 60px rgba(99,102,241,0.15), 0 20px 60px rgba(0,0,0,0.5)",
      }}>

        {/* ── PENDING ── */}
        {status === "pending" && (
          <>
            {/* Logo */}
            <div style={{
              width: 60, height: 60, borderRadius: 15,
              background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px", fontSize: 26, fontWeight: 900, color: "#fff",
            }}>N</div>

            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: "0 0 8px" }}>
              Awaiting Approval{dots}
            </h1>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 6px", lineHeight: 1.7 }}>
              Your account has been verified and is pending admin approval.
            </p>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#818cf8", margin: "0 0 28px" }}>
              {userEmail}
            </p>

            {/* Steps */}
            <div style={{
              background: "rgba(10,11,15,0.6)",
              border: "1px solid rgba(99,102,241,0.15)",
              borderRadius: 12, padding: "16px 20px",
              marginBottom: 24, textAlign: "left",
            }}>
              {[
                { icon: "✅", label: "Email verified",         done: true  },
                { icon: "⏳", label: "Admin approval pending", done: false },
                { icon: "🔓", label: "Access granted",         done: false },
                { icon: "📊", label: "Dashboard ready",        done: false },
              ].map((step) => (
                <div key={step.label} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "8px 0",
                  borderBottom: step.label !== "Dashboard ready" ? "1px solid rgba(99,102,241,0.08)" : "none",
                }}>
                  <span style={{ fontSize: 16, minWidth: 24 }}>{step.icon}</span>
                  <span style={{
                    fontSize: 13,
                    color: step.done ? "#4ade80" : "#64748b",
                    fontWeight: step.done ? 600 : 400,
                  }}>{step.label}</span>
                  {step.done && (
                    <span style={{ marginLeft: "auto", fontSize: 10, color: "#4ade80", fontWeight: 700 }}>DONE</span>
                  )}
                </div>
              ))}
            </div>

            {/* Nebula badge */}
            <div style={{
              background: "rgba(99,102,241,0.08)",
              border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: 8, padding: "10px 16px",
              marginBottom: 24, fontSize: 12, color: "#818cf8", lineHeight: 1.6,
            }}>
              ✦ <strong>Nebula Finance AI</strong> — Treasury Intelligence crafted by experts from Great Britain
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => doCheck(userEmail)} disabled={checking} style={{
                padding: "9px 20px", borderRadius: 8,
                border: "1px solid rgba(99,102,241,0.3)",
                background: "rgba(99,102,241,0.1)",
                color: "#818cf8", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>
                {checking ? "Checking…" : "↻ Check Status"}
              </button>
              <button onClick={handleLogout} style={{
                padding: "9px 20px", borderRadius: 8,
                border: "1px solid rgba(248,113,113,0.2)",
                background: "rgba(248,113,113,0.08)",
                color: "#f87171", fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}>
                Logout
              </button>
            </div>
          </>
        )}

        {/* ── APPROVED — 10 second countdown ── */}
        {status === "approved" && (
          <>
            {/* Pulse ring */}
            <div style={{ position: "relative", width: 80, height: 80, margin: "0 auto 24px" }}>
              <div style={{
                position: "absolute", inset: 0, borderRadius: "50%",
                border: "2px solid rgba(74,222,128,0.3)",
                animation: "ping 1.5s ease-out infinite",
              }} />
              <div style={{
                width: 80, height: 80, borderRadius: "50%",
                background: "rgba(74,222,128,0.1)",
                border: "1px solid rgba(74,222,128,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 32,
              }}>✅</div>
            </div>

            <style>{`
              @keyframes ping {
                0% { transform: scale(1); opacity: 1; }
                100% { transform: scale(1.8); opacity: 0; }
              }
              @keyframes spin { to { transform: rotate(360deg); } }
              @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <h1 style={{ fontSize: 24, fontWeight: 800, color: "#4ade80", margin: "0 0 8px", animation: "fadeIn 0.5s ease" }}>
              Access Approved!
            </h1>
            <p style={{ fontSize: 14, color: "#94a3b8", margin: "0 0 28px", lineHeight: 1.7 }}>
              Welcome to Nebula Finance AI. Your treasury dashboard is being prepared.
            </p>

            {/* Countdown circle */}
            <div style={{ position: "relative", width: 100, height: 100, margin: "0 auto 24px" }}>
              <svg width="100" height="100" style={{ transform: "rotate(-90deg)" }}>
                <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(74,222,128,0.1)" strokeWidth="6" />
                <circle cx="50" cy="50" r="44" fill="none"
                  stroke="#4ade80" strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 44}`}
                  strokeDashoffset={`${2 * Math.PI * 44 * (1 - countdown / 10)}`}
                  style={{ transition: "stroke-dashoffset 1s linear" }}
                />
              </svg>
              <div style={{
                position: "absolute", inset: 0,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
              }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: "#4ade80" }}>{countdown}</span>
                <span style={{ fontSize: 9, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>seconds</span>
              </div>
            </div>

            {/* Loading steps */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {[
                { label: "Loading company profile",   done: countdown <= 9 },
                { label: "Fetching market data",       done: countdown <= 7 },
                { label: "Preparing AI insights",      done: countdown <= 5 },
                { label: "Building your dashboard",    done: countdown <= 2 },
              ].map((step) => (
                <div key={step.label} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "6px 12px", borderRadius: 8,
                  background: step.done ? "rgba(74,222,128,0.08)" : "rgba(10,11,15,0.4)",
                  border: `1px solid ${step.done ? "rgba(74,222,128,0.2)" : "rgba(99,102,241,0.1)"}`,
                  transition: "all 0.5s ease",
                }}>
                  {step.done ? (
                    <span style={{ fontSize: 14 }}>✅</span>
                  ) : (
                    <div style={{
                      width: 14, height: 14, border: "2px solid #6366f1",
                      borderTopColor: "transparent", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite", flexShrink: 0,
                    }} />
                  )}
                  <span style={{ fontSize: 12, color: step.done ? "#4ade80" : "#64748b" }}>
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            <p style={{ fontSize: 11, color: "#374151" }}>
              Redirecting to your dashboard in {countdown} second{countdown !== 1 ? "s" : ""}…
            </p>
          </>
        )}

        {/* ── REJECTED ── */}
        {status === "rejected" && (
          <>
            <div style={{
              width: 64, height: 64, borderRadius: 16,
              background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px", fontSize: 28,
            }}>❌</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: "0 0 10px" }}>
              Access Denied
            </h1>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 24px", lineHeight: 1.7 }}>
              Your registration was not approved. Please contact{" "}
              <a href="mailto:founder@nebulafinance.net" style={{ color: "#818cf8" }}>
                founder@nebulafinance.net
              </a>
            </p>
            <button onClick={handleLogout} style={{
              padding: "10px 24px", borderRadius: 8, border: "none",
              background: "#6366f1", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
